import { Campaign } from '@hprofits/api';
import type { CampaignGroupId } from '@hprofits/shared/types';
import type { BreadcrumbItem } from '@hprofits/ui';
import { hasValue, isEmpty } from '@hprofits/utils/fp/optional';
import { ComputedRef, Ref, toValue } from 'vue';
import { computed } from 'vue';
import type { RouteLocation } from 'vue-router';
import { useRoute } from 'vue-router';

import { campaignRoutes } from '@/modules/common/route/constants';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';

export type UseCampaignBreadcrumbsOptions = Readonly<{
  campaignGroupId: CampaignGroupId;
  campaignGroupData: Readonly<Ref<Campaign | null>>;
}>;

export type UseCampaignBreadcrumbsReturn = ComputedRef<{
  backRoute: RouteLocation;
  breadcrumbs: ReadonlyArray<BreadcrumbItem> | null;
}>;

export const useCampaignBreadcrumbs = ({
  campaignGroupId,
  campaignGroupData,
}: UseCampaignBreadcrumbsOptions): UseCampaignBreadcrumbsReturn => {
  const route = useRoute();
  const routeResolver = useRouteResolverCampaigns();

  // TODO: Refactor this shit
  const currentElementName = computed(() => {
    const routeName = route.name ? String(route.name) : undefined;

    if (routeName?.includes('summary')) {
      return null;
    }

    if (routeName?.includes('target')) {
      const targetId = route.params.targetId as string;
      const target = toValue(campaignGroupData)?.targetGroups?.find((t) => String(t.id) === targetId);
      return target?.name ?? `Target #${targetId}`;
    }

    if (routeName?.includes('creative')) {
      const creativeId = route.params.creativeId as string;
      const creative = toValue(campaignGroupData)?.creativeGroups?.find((c) => String(c.id) === creativeId);
      return creative?.name ?? `Creative #${creativeId}`;
    }

    if (routeName?.includes('ad')) {
      const adId = route.params.adId as string;
      const ad = toValue(campaignGroupData)?.adGroups?.find((a) => String(a.id) === adId);
      return ad?.name ?? `Ad #${adId}`;
    }

    return null;
  });

  const isSummaryRoute = computed(() => String(route.name) === campaignRoutes.summary);

  return computed(() => {
    const campaignName = toValue(campaignGroupData)?.name;

    if (isEmpty(campaignName)) {
      return {
        backRoute: routeResolver.list(),
        breadcrumbs: null,
      };
    }

    const currentElement = toValue(currentElementName);
    const summaryPath = routeResolver.summary(campaignGroupId);

    const breadcrumbs: ReadonlyArray<BreadcrumbItem> = hasValue(currentElement)
      ? [{ label: campaignName, to: summaryPath }, { label: currentElement }]
      : [{ label: campaignName }];

    return {
      backRoute: toValue(isSummaryRoute) ? routeResolver.list() : summaryPath,
      breadcrumbs,
    };
  });
};
